import { API_BASE_URL } from '../constants/api';

class DataImportService {
  /**
   * Upload a file for preview — returns headers, sample rows, suggested mappings
   */
  async previewFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/data-import/preview`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to preview file');
    }
    return data;
  }

  /**
   * Execute the import with file + mapping + event config.
   * Streams SSE progress events back via onProgress callback.
   * Returns the final result.
   */
  async executeImport(file, mapping, eventConfig, onProgress) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('eventConfig', JSON.stringify(eventConfig));

    const response = await fetch(`${API_BASE_URL}/data-import/execute`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || 'Import failed');
    }

    // Read SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;
    let done = false;

    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (done) break;

      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress' && onProgress) {
              onProgress(data);
            } else if (data.type === 'complete') {
              finalResult = data;
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
    }

    if (!finalResult) {
      throw new Error('Import stream ended without completion');
    }

    return { success: true, data: finalResult };
  }

  /**
   * Get import history
   */
  async getHistory() {
    const response = await fetch(`${API_BASE_URL}/data-import/history`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch history');
    }
    return data;
  }

  /**
   * Delete all imported data, or a specific batch
   */
  async deleteImported(batchId = null) {
    const url = batchId
      ? `${API_BASE_URL}/data-import/imported?batchId=${encodeURIComponent(batchId)}`
      : `${API_BASE_URL}/data-import/imported`;

    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete imported data');
    }
    return data;
  }

  /**
   * Bulk archive specific patients by their patientIds (soft-delete for HIPAA retention)
   */
  async bulkArchive(patientIds) {
    const response = await fetch(`${API_BASE_URL}/data-import/bulk-archive`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patientIds }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to archive patients');
    }
    return data;
  }
}

export default new DataImportService();
